import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { SecurityEvents, SecureLog } from './utils/SecurityLogger';
import { AnimatePresence, motion } from 'framer-motion';
import { ToastProvider } from './utils/ToastContext';

// Import your components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import InstituteSelection from './components/InstituteSelection';
import ProfileSetup from './components/ProfileSetup';
import PaymentStatus from './components/PaymentStatus';

// ======================== EXTERNAL REDIRECT ========================
// Pages migrated to static landing site at residotech.com.
// These routes redirect visitors to the corresponding static pages.
function ExternalRedirect({ to }) {
  useEffect(() => { window.location.href = to; }, [to]);
  return null;
}

// Documentation components (Lazy Loaded)
const DocsLayout = React.lazy(() => import('./components/docs/DocsLayout'));
const GettingStarted = React.lazy(() => import('./components/docs/GettingStarted'));
const IntegrationGuide = React.lazy(() => import('./components/docs/IntegrationGuide'));
const SystemOverview = React.lazy(() => import('./components/docs/SystemOverview'));
const Architecture = React.lazy(() => import('./components/docs/Architecture'));
const BackendSession = React.lazy(() => import('./components/docs/BackendSession'));
const BackendWebhooks = React.lazy(() => import('./components/docs/BackendWebhooks'));
const BackendPush = React.lazy(() => import('./components/docs/BackendPush'));
const AndroidStructure = React.lazy(() => import('./components/docs/AndroidStructure'));
const AndroidPayments = React.lazy(() => import('./components/docs/AndroidPayments'));
const FirestoreSchema = React.lazy(() => import('./components/docs/FirestoreSchema'));
const DashboardComponents = React.lazy(() => import('./components/docs/DashboardComponents'));
const AdminBilling = React.lazy(() => import('./components/docs/AdminBilling'));
const Deployment = React.lazy(() => import('./components/docs/Deployment'));
const EnvVars = React.lazy(() => import('./components/docs/EnvVars'));
const AndroidScreens = React.lazy(() => import('./components/docs/AndroidScreens'));
const DatabaseSecurity = React.lazy(() => import('./components/docs/DatabaseSecurity'));
const Monitoring = React.lazy(() => import('./components/docs/Monitoring'));
const OperationalTools = React.lazy(() => import('./components/docs/OperationalTools'));
const AdminLogin = React.lazy(() => import('./components/docs/AdminLogin'));
const AdminProtectedDocs = React.lazy(() => import('./components/docs/AdminProtectedDocs'));

// ======================== PRODUCTION CONSOLE OVERRIDE ========================
// [SECURITY] Suppress raw console output in production to avoid leaking
// internal state. SecurityLogger.SecureLog is the approved logging channel.
if (process.env.NODE_ENV === 'production') {
  // eslint-disable-next-line no-console
  console.log = () => { };
  // eslint-disable-next-line no-console
  console.warn = () => { };
  // eslint-disable-next-line no-console
  console.debug = () => { };
  // Leave console.error intact — still useful for unhandled error catching
}

// ======================== IDLE TIMEOUT HOOK ========================
// [SECURITY] Auto sign-out after 30 minutes of inactivity.
// The Android app has biometric/device-level security; the web needs its own.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

function useIdleTimeout(onTimeout) {
  const timerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onTimeoutRef.current?.();
    }, IDLE_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(event => window.addEventListener(event, resetTimer, { passive: true }));
    resetTimer(); // Start the timer

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [resetTimer]);
}

// ======================== OFFLINE BANNER ========================
// [FEATURE] Shows a persistent banner when the user loses connectivity.
// Critical for iOS Safari users who frequently switch between apps.
function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleOnline = () => { setIsOffline(false); setDismissed(false); };
    const handleOffline = () => { setIsOffline(true); setDismissed(false); };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline || dismissed) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'linear-gradient(135deg, #D97706, #F59E0B)',
        color: '#0F172A',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontFamily: "'Inter', sans-serif",
        fontSize: '14px',
        fontWeight: 600,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M12 12h.01" />
          <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        <span>You're offline. Some features may be unavailable.</span>
      </div>
      <button
        onClick={() => setDismissed(true)}
        style={{
          background: 'none',
          border: 'none',
          color: '#0F172A',
          cursor: 'pointer',
          padding: '4px',
          opacity: 0.7,
          fontSize: '18px',
          lineHeight: 1,
        }}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

// ======================== iOS ADD TO HOME SCREEN BANNER ========================
// [FEATURE] iOS Safari doesn't fire beforeinstallprompt — show a custom banner
// with Safari-specific instructions. Shown once per browser session.
function IOSInstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone === true;
    const isDismissed = localStorage.getItem('ios_install_dismissed') === '1';
    if (isIOS && !isStandalone && !isDismissed) {
      // Slight delay — don't interrupt page load
      const t = setTimeout(() => setShow(true), 2500);
      return () => clearTimeout(t);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('ios_install_dismissed', '1');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 'max(16px, env(safe-area-inset-bottom))',
      left: 16, right: 16, zIndex: 9998,
      background: 'rgba(15,23,42,0.95)',
      border: '1px solid rgba(245,158,11,0.3)',
      borderRadius: 20, padding: '16px 20px',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      fontFamily: "'Inter', sans-serif",
      animation: 'slide-up-banner 0.4s cubic-bezier(0.22,1,0.36,1) both',
    }}>
      <style>{`
        @keyframes slide-up-banner {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/assets/ic_placeholder_avatar.png" alt="Resido" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <div>
            <p style={{ color: '#F8FAFC', fontWeight: 700, fontSize: 14, margin: 0 }}>Install Resido</p>
            <p style={{ color: '#94A3B8', fontSize: 12, margin: 0 }}>Add to your Home Screen for the best experience</p>
          </div>
        </div>
        <button onClick={handleDismiss} style={{
          background: 'none', border: 'none', color: '#64748B',
          cursor: 'pointer', fontSize: 20, padding: '0 4px', lineHeight: 1,
        }}>×</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#CBD5E1', fontSize: 13, lineHeight: 1.6 }}>
        <span>Tap</span>
        {/* Safari Share icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
          <polyline points="16 6 12 2 8 6" />
          <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
        <span>then <strong style={{ color: '#F8FAFC' }}>"Add to Home Screen"</strong></span>
      </div>
    </div>
  );
}

// ======================== SKELETON LOADER ========================
// [FEATURE] Shimmer skeleton matching the dashboard layout instead of a plain spinner.
function DashboardSkeleton() {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0F172A',
      padding: '24px',
      fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .skeleton-pulse {
          background: linear-gradient(90deg, #1E293B 0%, #334155 50%, #1E293B 100%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
          border-radius: 12px;
        }
      `}</style>
      {/* Top Bar Skeleton */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div className="skeleton-pulse" style={{ width: '120px', height: '32px' }} />
        <div className="skeleton-pulse" style={{ width: '36px', height: '36px', borderRadius: '50%' }} />
      </div>
      {/* Title Skeleton */}
      <div className="skeleton-pulse" style={{ width: '200px', height: '28px', marginBottom: '8px' }} />
      <div className="skeleton-pulse" style={{ width: '260px', height: '16px', marginBottom: '32px' }} />
      {/* Stat Cards Skeleton */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton-pulse" style={{ height: '100px' }} />
        ))}
      </div>
      {/* Table Skeleton */}
      <div className="skeleton-pulse" style={{ height: '200px' }} />
      {/* Bottom Nav Skeleton */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        height: '64px', background: '#1E293B',
        display: 'flex', justifyContent: 'space-around', alignItems: 'center',
        borderTop: '1px solid #334155',
      }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-pulse" style={{ width: '40px', height: '40px', borderRadius: '10px' }} />
        ))}
      </div>
    </div>
  );
}

// ======================== PAGE TRANSITIONS ========================
// NOTE: AnimatePresence mode='wait' was removed because it caused white-flash
// during page transitions. The old route unmounts before the new one mounts,
// exposing the body background for a frame. Instant route switches are preferred.

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0F172A' }}>
      <Routes location={location}>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />

        {/* Migrated to residotech.com — external redirects */}
        <Route path="/faqs" element={<ExternalRedirect to="https://residotech.com/faqs.html" />} />
        <Route path="/privacy-policy" element={<ExternalRedirect to="https://residotech.com/privacy-policy.html" />} />
        <Route path="/terms-of-service" element={<ExternalRedirect to="https://residotech.com/terms-of-service.html" />} />
        <Route path="/disclaimer" element={<ExternalRedirect to="https://residotech.com/disclaimer.html" />} />
        <Route path="/support" element={<ExternalRedirect to="https://residotech.com/support.html" />} />

        {/* Documentation Login */}
        <Route path="/docs/login" element={
          <React.Suspense fallback={<DashboardSkeleton />}>
            <AdminLogin />
          </React.Suspense>
        } />

        {/* Documentation Routes (Protected) */}
        <Route element={
          <React.Suspense fallback={<DashboardSkeleton />}>
            <AdminProtectedDocs />
          </React.Suspense>
        }>
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={<Navigate to="/docs/overview" replace />} />
            <Route path="getting-started" element={<GettingStarted />} />
            <Route path="overview" element={<SystemOverview />} />
            <Route path="integration-guide" element={<IntegrationGuide />} />
            <Route path="architecture" element={<Architecture />} />
            {/* Backend */}
            <Route path="backend/session" element={<BackendSession />} />
            <Route path="backend/webhooks" element={<BackendWebhooks />} />
            <Route path="backend/push" element={<BackendPush />} />
            {/* Android */}
            <Route path="android/structure" element={<AndroidStructure />} />
            <Route path="android/payments" element={<AndroidPayments />} />
            <Route path="android/screens" element={<AndroidScreens />} />
            {/* Data */}
            <Route path="data/schema" element={<FirestoreSchema />} />
            <Route path="database/security" element={<DatabaseSecurity />} />
            {/* Admin */}
            <Route path="dashboard/components" element={<DashboardComponents />} />
            <Route path="admin/billing" element={<AdminBilling />} />
            {/* Operations */}
            <Route path="deployment" element={<Deployment />} />
            <Route path="env-vars" element={<EnvVars />} />
            <Route path="monitoring" element={<Monitoring />} />
            <Route path="tools" element={<OperationalTools />} />
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/docs/overview" replace />} />
          </Route>
        </Route>

        {/* Private Routes - Signup Flow */}
        <Route
          path="/institute-selection"
          element={<PrivateRoute><InstituteSelection /></PrivateRoute>}
        />
        <Route
          path="/profile-setup"
          element={<PrivateRoute><ProfileSetup /></PrivateRoute>}
        />

        {/* Private Routes - Dashboard */}
        <Route
          path="/dashboard"
          element={<PrivateRoute><Dashboard /></PrivateRoute>}
        />
        <Route
          path="/payment-status"
          element={<PrivateRoute><PaymentStatus /></PrivateRoute>}
        />

        {/* Fallback Route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

// ======================== PRIVATE ROUTE ========================
// Uses onAuthStateChanged to properly wait for Firebase Auth to restore
// the session after full-page navigations (e.g. payment gateway redirect).
function PrivateRoute({ children }) {
  const [authState, setAuthState] = useState({ loading: true, user: null });

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthState({ loading: false, user });
    });
    return () => unsubscribe();
  }, []);

  if (authState.loading) {
    return <DashboardSkeleton />;
  }

  return authState.user ? children : <Navigate to="/login" />;
}

// ======================== MAIN APP ========================
function App() {
  // --- CSP Violation Listener (Security Audit Trail) ---
  useEffect(() => {
    const handleCSPViolation = (e) => {
      // [SECURITY] Log to both Analytics and dev console
      SecurityEvents.cspViolation(
        e.violatedDirective || 'unknown',
        e.blockedURI || 'unknown'
      );
      SecureLog.warn(
        `[CSP Violation] Directive: ${e.violatedDirective}, Blocked: ${e.blockedURI}, Source: ${e.sourceFile || 'unknown'}:${e.lineNumber || '?'}`
      );
    };
    document.addEventListener('securitypolicyviolation', handleCSPViolation);
    return () => document.removeEventListener('securitypolicyviolation', handleCSPViolation);
  }, []);

  // --- Session Idle Timeout ---
  useIdleTimeout(() => {
    const auth = getAuth();
    if (auth.currentUser) {
      SecureLog.warn('[Security] Session timed out after 30 minutes of inactivity.');
      SecurityEvents.sessionExpired();
      signOut(auth).then(() => {
        window.location.href = '/login?reason=session_timeout';
      }).catch((err) => {
        SecureLog.error('[Security] Failed to sign out on timeout:', err);
      });
    }
  });

  return (
    <ToastProvider>
      <Router>
        <OfflineBanner />
        <IOSInstallBanner />
        <AnimatedRoutes />
      </Router>
    </ToastProvider>
  );
}

export default App;